import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { IconCancel, IconSave } from '../icons/HvacIcons';
import './hv-modal.css';

export interface NodePatch {
  id: string;
  displayName: string | null;
  category: string | null;
  sortOrder: number;
}

interface Props {
  plantId: string;
  node: { id: string; symbol: string; displayName: string | null; category: string | null; sortOrder: number };
  onSaved(patch: NodePatch): void;
  onDeleted(nodeId: string): void;
  onClose(): void;
}

/** Node rename + soft-delete. Symbol is immutable (deterministic-UUID anchor)
 *  and shown read-only for context. */
export function NodeEditModal({ plantId, node, onSaved, onDeleted, onClose }: Props) {
  const [displayName, setDisplayName] = useState(node.displayName ?? '');
  const [category, setCategory] = useState(node.category ?? '');
  const [sortOrder, setSortOrder] = useState(String(node.sortOrder));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      const clear: string[] = [];
      const dn = displayName.trim();
      if (dn === '') clear.push('displayName');
      else body.displayName = dn;
      const cat = category.trim();
      if (cat === '') clear.push('category');
      else body.category = cat;
      const so = Number(sortOrder);
      if (!Number.isFinite(so) || !Number.isInteger(so)) throw new Error('Sortierung muss eine ganze Zahl sein.');
      body.sortOrder = so;
      if (clear.length > 0) body.clearFields = clear;
      const res = await api.patch<NodePatch>(`/api/plants/${plantId}/nodes/${node.id}`, body);
      onSaved(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Speichern fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  const del = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/plants/${plantId}/nodes/${node.id}`);
      onDeleted(node.id);
    } catch (e) {
      const msg = e instanceof ApiError ? `Löschen fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      setError(msg);
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Node bearbeiten</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>Symbol</label>
            <input type="text" value={node.symbol} disabled />
          </div>
          <div class="hv-modal__field">
            <label>Anzeigename</label>
            <input type="text" value={displayName} onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} placeholder="optional" />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Kategorie</label>
              <input type="text" value={category} onInput={(e) => setCategory((e.target as HTMLInputElement).value)} placeholder="z.B. heizkreis" />
            </div>
            <div class="hv-modal__field">
              <label>Sortierung</label>
              <input type="text" value={sortOrder} onInput={(e) => setSortOrder((e.target as HTMLInputElement).value)} />
            </div>
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          {confirmDelete ? (
            <>
              <span class="hv-modal__confirm-label">Node inklusive aller Kinder und Leaves soft-löschen?</span>
              <button type="button" class="hv-btn hv-btn--danger" onClick={del} disabled={saving}>Ja, löschen</button>
              <button type="button" class="hv-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" class="hv-btn hv-btn--danger-outline" onClick={() => setConfirmDelete(true)} disabled={saving}>Löschen</button>
              <div style={{ flex: 1 }} />
              <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
              <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
